// frontend/src/services/geogridCache.js
/**
 * Единый кэш данных геосетки и настроек по ключу офиса (kluch).
 * Гарантии:
 * 1. Ровно один сетевой запрос замера и один запрос настроек на офис за открытие страницы.
 * 2. Отклонённые промисы (ошибки сети/сервера) НЕ кэшируются: при повторном заходе делается новый запрос.
 * 3. Поддерживается сброс всего кэша (при выходе пользователя или в тестах).
 * 4. Поддерживается точечная инвалидация настроек при их сохранении в модальном окне.
 */

import reputationService from './reputationService';

const cache = new Map();

/**
 * Получить данные замера и настройки для офиса с кэшированием.
 * @param {string} kluch - Ключ офиса (напр. 'coral-springs', 'hallandale-beach')
 * @returns {Promise<{ geogrid: object, settings: object }>}
 */
export const fetchGeogridData = (kluch) => {
  if (!kluch) {
    return Promise.resolve({ geogrid: null, settings: null });
  }

  if (cache.has(kluch)) {
    return cache.get(kluch);
  }

  const promise = Promise.all([
    reputationService.getGeogridByKluch(kluch),
    typeof reputationService.getGeogridSettings === 'function'
      ? reputationService.getGeogridSettings(kluch).catch(() => null)
      : Promise.resolve(null),
  ])
    .then(([geogrid, settings]) => {
      return { geogrid, settings };
    })
    .catch((err) => {
      // 🔴 Отклонённые промисы удаляются из кэша, чтобы следующий заход повторил запрос
      cache.delete(kluch);
      throw err;
    });

  cache.set(kluch, promise);
  return promise;
};

/**
 * Сбросить весь кэш (при выходе из системы или в beforeEach тестов).
 */
export const clearGeogridCache = () => {
  cache.clear();
};

/**
 * Обновить настройки в кэше для указанного офиса без повторного замера.
 * @param {string} kluch
 * @param {object} newSettings
 */
export const updateGeogridSettingsInCache = (kluch, newSettings) => {
  if (!kluch) return;
  if (cache.has(kluch)) {
    cache.get(kluch).then((data) => {
      if (data) {
        data.settings = newSettings;
      }
    }).catch(() => {});
  }
  // 🔴 Если записи в кэше нет (например, прошлый запрос замера упал с ошибкой),
  // НЕ кэшируем { geogrid: null, settings: newSettings }, так как это навсегда покажет
  // «не измерялось» у замеренного офиса. Следующий fetchGeogridData запросит и замер, и настройки.
};

const geogridCache = {
  fetchGeogridData,
  clearGeogridCache,
  updateGeogridSettingsInCache,
};

export default geogridCache;
